import type {
  AggregateRow,
  PortfolioTotals,
} from "./hooks/useBuildingAggregates";
// trendUtils is untyped JS; TREND_LEVELS maps a signed level to its label.
import { TREND_LEVELS } from "./trendUtils";

/** Format a per-sensor rate: 1 decimal, dropping a trailing ".0" (2.5, 2, 0.3). */
function fmtRate(n: number): string {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/**
 * The triage bar for the needs-attention feeds: only surface things that
 * genuinely need attention, meaning Elevated priority or higher, or actively
 * worsening. A low-priority item that's steady or improving is excluded.
 */
export function needsAttention(
  priorityBand: string | null | undefined,
  trendLevel: number | null | undefined
): boolean {
  const elevatedPlus =
    priorityBand === "elevated" ||
    priorityBand === "high" ||
    priorityBand === "critical";
  return elevatedPlus || (trendLevel ?? 0) > 0;
}

/** True once a building has enough resolved signal to appear in the feed:
 *  trend/priority (alarm-based) or connectivity (offline). Lets the feed
 *  populate as data streams in rather than waiting on date-range alert counts. */
export function hasAttentionData(a: AggregateRow | undefined): boolean {
  if (!a) return false;
  return (
    a.priorityScore != null || a.trendLevel != null || a.onlineCount != null
  );
}

/**
 * Pure helpers that turn the per-building aggregates (from useBuildingAggregates)
 * into ranked, human-readable insights. No React here so they can be reused by
 * the command-center "needs attention" feed, the in-app weekly digest, and unit
 * tests. The backend weekly-email job mirrors `buildPortfolioDigest`'s shape.
 */

export type Building = {
  buildingId: string;
  name: string;
  LatLong: [number, number];
};

export type AttentionIssueKind =
  | "shutdowns"
  | "alerts"
  | "declining"
  | "offline";

export type AttentionIssue = {
  kind: AttentionIssueKind;
  /** Higher = more urgent. Drives row ordering and the row's tone. */
  severity: number;
  /** Short chip text, e.g. "3 alerts" or "2 of 5 offline". */
  text: string;
};

export type AttentionItem = {
  buildingId: string;
  name: string;
  /** Primary feed rank = the building's 0-100 priority score. */
  severity: number;
  priorityScore: number;
  priorityBand: string | null;
  trendLevel: number | null;
  issues: AttentionIssue[];
};

/** Base urgency per issue kind. Counts nudge severity within a kind so a
 *  property with more alerts/shutdowns sorts above one with fewer. */
const KIND_BASE: Record<AttentionIssueKind, number> = {
  shutdowns: 400,
  alerts: 300,
  declining: 200,
  offline: 100,
};

/**
 * One row per property that needs attention, sorted most-urgent first. A
 * property can carry several issue chips (e.g. offline + declining); the row's
 * severity is the max across them. Properties still loading or with no issues
 * are omitted. Alert/shutdown counts honor the active date range (they come
 * straight from the AggregateRow); trend is the hook's own 28-day window.
 */
export function buildAttentionItems(
  buildings: Building[],
  aggregates: Map<string, AggregateRow>
): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const b of buildings) {
    const a = aggregates.get(b.buildingId);
    // Appear as soon as any signal resolves (don't wait on date-range counts).
    if (!a || !hasAttentionData(a)) continue;

    const issues: AttentionIssue[] = [];

    // Counts are shown per-sensor so properties of different sizes compare
    // fairly (a big property naturally has more raw alerts without being worse).
    const sensors = a.unitsCount && a.unitsCount > 0 ? a.unitsCount : 1;

    if (a.shutdownsCount != null && a.shutdownsCount > 0) {
      issues.push({
        kind: "shutdowns",
        severity: KIND_BASE.shutdowns + a.shutdownsCount,
        text: `${fmtRate(a.shutdownsCount / sensors)} safety shut offs/sensor`,
      });
    }

    if (a.alertsCount != null && a.alertsCount > 0) {
      issues.push({
        kind: "alerts",
        severity: KIND_BASE.alerts + a.alertsCount,
        text: `${fmtRate(a.alertsCount / sensors)} alerts/sensor`,
      });
    }

    if ((a.trendLevel ?? 0) > 0) {
      issues.push({
        kind: "declining",
        severity: KIND_BASE.declining,
        // Same wording as the trend badges (Slipping / Worsening / Deteriorating).
        text:
          (TREND_LEVELS as Record<string, { label: string }>)[
            String(a.trendLevel)
          ]?.label ?? "Worsening",
      });
    }

    if (
      a.onlineCount != null &&
      a.unitsCount != null &&
      a.onlineCount < a.unitsCount
    ) {
      const offline = a.unitsCount - a.onlineCount;
      issues.push({
        kind: "offline",
        severity: KIND_BASE.offline + offline,
        text: `${offline} of ${a.unitsCount} offline`,
      });
    }

    if (issues.length === 0) continue;
    // Only surface things that actually need attention (skip low/none priority
    // that isn't worsening, for example a low, improving property).
    if (!needsAttention(a.priorityBand, a.trendLevel)) continue;

    issues.sort((x, y) => y.severity - x.severity);
    const priorityScore = a.priorityScore ?? 0;
    items.push({
      buildingId: b.buildingId,
      name: b.name,
      severity: priorityScore,
      priorityScore,
      priorityBand: a.priorityBand ?? null,
      trendLevel: a.trendLevel ?? null,
      issues,
    });
  }

  // Rank by the danger-based priority score first; tie-break on the worst issue
  // (so offline-only properties, which carry no danger score, still order
  // sensibly among themselves).
  items.sort((x, y) => {
    if (y.priorityScore !== x.priorityScore) {
      return y.priorityScore - x.priorityScore;
    }
    return (y.issues[0]?.severity ?? 0) - (x.issues[0]?.severity ?? 0);
  });
  return items;
}

export type DigestPerBuilding = {
  buildingId: string;
  name: string;
  unitsCount: number | null;
  onlineCount: number | null;
  offlineCount: number | null;
  alertsCount: number | null;
  shutdownsCount: number | null;
  hasPCO: boolean;
  trendStatus: string | null;
  trendLevel: number | null;
  priorityScore: number | null;
  priorityBand: string | null;
};

export type PortfolioDigest = {
  totals: PortfolioTotals;
  /** Total sensors currently offline across the portfolio. */
  offlineCount: number;
  /** Properties needing attention, most urgent first. */
  attention: AttentionItem[];
  perBuilding: DigestPerBuilding[];
  startDate: string | null;
  endDate: string | null;
};

/**
 * The single summary object consumed by the in-app weekly digest view (and
 * mirrored by the backend email job). Combines portfolio totals, the attention
 * feed, and a per-property breakdown sorted to match the feed's urgency.
 */
export function buildPortfolioDigest(
  buildings: Building[],
  aggregates: Map<string, AggregateRow>,
  totals: PortfolioTotals,
  startDate: string | null,
  endDate: string | null
): PortfolioDigest {
  const attention = buildAttentionItems(buildings, aggregates);
  const offlineCount = Math.max(0, totals.units - totals.onlineSensors);

  const severityByBuilding = new Map(
    attention.map((a) => [a.buildingId, a.severity])
  );

  const perBuilding: DigestPerBuilding[] = buildings.map((b) => {
    const a = aggregates.get(b.buildingId);
    const unitsCount = a?.unitsCount ?? null;
    const onlineCount = a?.onlineCount ?? null;
    return {
      buildingId: b.buildingId,
      name: b.name,
      unitsCount,
      onlineCount,
      offlineCount:
        unitsCount != null && onlineCount != null
          ? unitsCount - onlineCount
          : null,
      alertsCount: a?.alertsCount ?? null,
      shutdownsCount: a?.shutdownsCount ?? null,
      hasPCO: a?.hasPCO ?? false,
      trendStatus: a?.trendStatus ?? null,
      trendLevel: a?.trendLevel ?? null,
      priorityScore: a?.priorityScore ?? null,
      priorityBand: a?.priorityBand ?? null,
    };
  });

  // Sort by attention severity (descending), then by name for stability.
  perBuilding.sort((x, y) => {
    const sx = severityByBuilding.get(x.buildingId) ?? -1;
    const sy = severityByBuilding.get(y.buildingId) ?? -1;
    if (sx !== sy) return sy - sx;
    return x.name.localeCompare(y.name, undefined, { numeric: true });
  });

  return {
    totals,
    offlineCount,
    attention,
    perBuilding,
    startDate,
    endDate,
  };
}

/** Tailwind tone (matching StatusDot's Tone) for an issue kind. */
export function issueTone(
  kind: AttentionIssueKind
): "critical" | "warning" | "info" | "offline" {
  switch (kind) {
    case "shutdowns":
      return "info";
    case "alerts":
      return "critical";
    case "declining":
      return "warning";
    case "offline":
      return "offline";
  }
}
